import {
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogClose,
  MatDialogConfig,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { forkJoin } from 'rxjs';
import { finalize, first, map } from 'rxjs/operators';
import { ApiTypes } from '@app/api/api-types';
import { AccountService } from '@core/providers/account/account.service';
import {
  AiTranslateBackendService,
  AiTranslateTargetType,
} from '@core/providers/backend/ai-translate-backend.service';
import { ContextService } from '@core/providers/context/context.service';
import { NotificationService } from '@core/providers/notification/notification.service';
import { AiDisclosureComponent } from '@shared/components/ai-disclosure/ai-disclosure.component';
import {
  DialogActionsDirective,
  DialogComponent,
  DialogContentDirective,
  DialogTitleDirective,
} from '@shared/components/dialog/dialog.component';
import { InfoFormBoxComponent } from '@shared/components/info-form-box/info-form-box.component';
import { InputWithCounterComponent } from '@shared/components/input-with-counter/input-with-counter.component';
import { TextareaWithCounterComponent } from '@shared/components/textarea-with-counter/textarea-with-counter.component';
import { TranslationLanguageListComponent } from '@shared/components/translation-language-list/translation-language-list.component';
import { AiUpsellPopoverDirective } from '@shared/directives/ai-upsell-popover.directive';
import { TooltipWhenTruncatedDirective } from '@shared/directives/tooltip-when-truncated.directive';
import { I18nSupportedLanguage } from '@shared/interfaces/i18n.interface';
import {
  FailedTranslation,
  FailedTranslationsByField,
  FailedTranslationsByLanguage,
  mergeFailedTranslations,
  readAiTranslations,
  failureOutlivesEdit,
  settleFailureOnEdit,
} from './ai-translation-outcome';
import { FailedTranslationNoteComponent } from './failed-translation-note.component';

/** Metadata describing a single translatable field shown in the dialog. */
export interface TranslateFieldDef {
  /** Key used in the translation map. */
  key: string;
  /** i18n key for the field label. */
  label: string;
  /** Optional interpolation params for the label translation. */
  labelParams?: Record<string, unknown>;
  /** Render as a textarea instead of a single-line input. */
  multiline?: boolean;
  /** Maximum character length enforced in the field. */
  maxLength?: number;
}

/** Translation map: fieldKey → per-language entries. */
export type TranslateDialogResult = Record<string, { lang: string; value: string }[]>;

export interface TranslateDialogData {
  /** Pre-translated dialog title (caller uses TranslateService.instant). */
  title: string;
  /** Field definitions. */
  fields: TranslateFieldDef[];
  /** Default-language values shown as the original reference. */
  sourceValues: Record<string, string>;
  /** Existing translations: fieldKey → per-language entries. */
  existingTranslations: Record<string, { lang: string; value: string }[]>;
  /** Active languages to translate (default language excluded). */
  languages: I18nSupportedLanguage[];
  /** Reference language displayed as the original. */
  defaultLanguage: I18nSupportedLanguage | null;
  /** Inactive languages shown in the collapsible section. */
  inactiveLanguages?: I18nSupportedLanguage[];
  /**
   * Optional readonly mask.
   * readonlyFields[langCode] = ['fieldKey', ...] — fields the user cannot edit.
   */
  readonlyFields?: Record<string, string[]>;
  /** Optional informational message shown at the top of the dialog content. */
  infoMessage?: string;
  /** Target entity type for the AI translation endpoint. Required to enable the "Suggest all" button. */
  aiTargetType?: AiTranslateTargetType;
  /** UUID of the account or venue for AI translation. Required to enable the "Suggest all" button. */
  aiTargetUuid?: string;
}

export const translateDialogConfig: MatDialogConfig = {
  width: '960px',
  maxWidth: '96vw',
  height: '90vh',
  maxHeight: '900px',
  disableClose: false,
  panelClass: 'translate-dialog-panel',
};

@Component({
  selector: 'app-translate-dialog',
  templateUrl: './translate-dialog.component.html',
  styleUrl: './translate-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    DialogComponent,
    DialogTitleDirective,
    DialogContentDirective,
    DialogActionsDirective,
    MatDialogClose,
    MatButton,
    MatIcon,
    FormsModule,
    InputWithCounterComponent,
    TextareaWithCounterComponent,
    TranslationLanguageListComponent,
    AiDisclosureComponent,
    AiUpsellPopoverDirective,
    FailedTranslationNoteComponent,
    InfoFormBoxComponent,
    TooltipWhenTruncatedDirective,
    TranslatePipe,
  ],
})
export class TranslateDialogComponent {
  protected readonly data = inject<TranslateDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef =
    inject<MatDialogRef<TranslateDialogComponent, TranslateDialogResult | undefined>>(MatDialogRef);
  private readonly aiTranslateService = inject(AiTranslateBackendService);
  private readonly accountService = inject(AccountService);
  private readonly contextService = inject(ContextService);
  private readonly notificationService = inject(NotificationService);
  private readonly destroyRef = inject(DestroyRef);

  /** Whether the current account has Foodback Intelligence enabled. */
  protected readonly isAiEnabled = toSignal(
    this.accountService.getAccountContext$().pipe(map(account => account.isAiEnabled)),
    { initialValue: false }
  );

  protected readonly fields: TranslateFieldDef[] = this.data.fields;
  protected readonly sourceValues = this.data.sourceValues;

  private readonly allLangs = [...this.data.languages, ...(this.data.inactiveLanguages ?? [])];

  private readonly activeCodesSet = new Set(this.data.languages.map(l => l.code));

  /** Mutable map: langCode → fieldKey → translated value. */
  protected readonly values = signal<Record<string, Record<string, string>>>(
    this.buildInitialValues()
  );

  protected readonly inactiveExpanded = signal(false);
  protected readonly isSuggesting = signal(false);
  /** Languages whose current text came from AI translation, so they can be disclosed as such. */
  protected readonly aiTranslatedCodes = signal<string[]>([]);
  /** Fields and languages the AI left unfilled, and why, until the admin edits them. */
  protected readonly failedTranslations = signal<FailedTranslationsByField>({});

  /** Rejected AI text put in a field to shorten, still longer than the field allows. */
  protected readonly hasTextOverLimit = computed(() =>
    Object.entries(this.failedTranslations()).some(([fieldKey, byLanguage]) =>
      Object.entries(byLanguage).some(([langCode, failure]) =>
        failureOutlivesEdit(failure, this.getValue(langCode, fieldKey))
      )
    )
  );

  /** True when there are 2+ fields — selects the focus-rail layout over the stacked list. */
  protected readonly isMultiField = computed(() => this.fields.length > 1);

  /** Currently selected language code in the rail layout. */
  protected readonly selectedLangCode = signal<string>(
    this.data.languages[0]?.code ?? this.data.inactiveLanguages?.[0]?.code ?? ''
  );

  /** Full language object for the currently selected language. */
  protected readonly selectedLang = computed<I18nSupportedLanguage | undefined>(() =>
    this.allLangs.find(l => l.code === this.selectedLangCode())
  );

  /** Per-language completion state including 'partial' for status dots. */
  protected readonly completionStateMap = computed(() => {
    const required = this.fields.filter(f => this.sourceValues[f.key]?.trim());
    const result: Record<string, 'ok' | 'partial' | 'none'> = {};
    for (const lang of this.allLangs) {
      const done = required.filter(f => this.isFieldDone(lang.code, f.key)).length;
      result[lang.code] = done === 0 ? 'none' : done === required.length ? 'ok' : 'partial';
    }
    if (this.data.defaultLanguage) {
      result[this.data.defaultLanguage.code] = 'ok';
    }
    return result;
  });

  /** Boolean completion map derived from completionStateMap (for backwards compat). */
  protected readonly completionMap = computed(() => {
    const states = this.completionStateMap();
    return Object.fromEntries(
      Object.entries(states).map(([code, state]) => [code, state === 'ok'])
    );
  });

  protected readonly totalCount = computed(
    () => this.data.languages.length + (this.data.defaultLanguage ? 1 : 0)
  );

  protected readonly completedCount = computed(() => {
    const map = this.completionMap();
    const activeCodes = new Set(this.data.languages.map(l => l.code));
    if (this.data.defaultLanguage) {
      activeCodes.add(this.data.defaultLanguage.code);
    }
    return [...activeCodes].filter(code => map[code]).length;
  });

  /** Number of required fields with a non-empty translation for the selected language. */
  protected readonly selectedLangFieldsDone = computed(() => {
    const code = this.selectedLangCode();
    const required = this.fields.filter(f => this.sourceValues[f.key]?.trim());
    return required.filter(f => this.isFieldDone(code, f.key)).length;
  });

  /** Total number of required (non-empty source) fields. */
  protected readonly requiredFieldCount = computed(
    () => this.fields.filter(f => this.sourceValues[f.key]?.trim()).length
  );

  /** All target language codes (active + inactive). */
  protected readonly allLangCodes = computed(() => this.allLangs.map(l => l.code));

  /**
   * AI target for the suggest endpoint. Falls back to the current account/venue
   * context when the caller doesn't pass one explicitly — the content being
   * translated always belongs to the active context.
   */
  private readonly aiTargetType = signal<AiTranslateTargetType | undefined>(this.data.aiTargetType);
  private readonly aiTargetUuid = signal<string | undefined>(this.data.aiTargetUuid);

  /** True when the AI endpoint is configured and there is source text to translate. */
  protected readonly canSuggest = computed(
    () =>
      Boolean(this.aiTargetType() && this.aiTargetUuid()) &&
      Object.values(this.sourceValues).some(v => v?.trim())
  );

  // ── Single-field adapters for app-translation-language-list ──────────────

  protected readonly singleFieldValues = computed<Record<string, string>>(() => {
    const key = this.fields[0]?.key;
    if (!key) {
      return {};
    }
    const vals = this.values();
    return Object.fromEntries(this.allLangs.map(lang => [lang.code, vals[lang.code]?.[key] ?? '']));
  });

  protected readonly singleFieldReadonlyLangs = computed<string[]>(() => {
    const key = this.fields[0]?.key;
    if (!key) {
      return [];
    }
    return this.allLangs
      .filter(lang => this.isReadonlyField(lang.code, key))
      .map(lang => lang.code);
  });

  protected readonly singleFieldFailures = computed<FailedTranslationsByLanguage>(() => {
    const key = this.fields[0]?.key;
    return key ? (this.failedTranslations()[key] ?? {}) : {};
  });

  protected onSingleFieldValuesChange(values: Record<string, string>): void {
    const key = this.fields[0]?.key;
    if (!key) {
      return;
    }
    for (const [code, value] of Object.entries(values)) {
      if (value !== this.getValue(code, key)) {
        this.setValue(code, key, value);
      }
    }
  }

  constructor() {
    if (this.fields.length <= 1) {
      this.dialogRef.updateSize('640px', translateDialogConfig.height);
    }

    if (!this.aiTargetType() || !this.aiTargetUuid()) {
      this.contextService
        .getContext$()
        .pipe(first(), takeUntilDestroyed(this.destroyRef))
        .subscribe(context => {
          if (ContextService.isAccountContext(context)) {
            this.aiTargetType.set('accounts');
          } else if (ContextService.isVenueContext(context)) {
            this.aiTargetType.set('venues');
          }
          this.aiTargetUuid.set(context.uuid);
        });
    }
  }

  protected isActiveLang(code: string): boolean {
    return this.activeCodesSet.has(code);
  }

  protected getValue(langCode: string, fieldKey: string): string {
    return this.values()[langCode]?.[fieldKey] ?? '';
  }

  protected setValue(langCode: string, fieldKey: string, value: string): void {
    this.values.update(current => ({
      ...current,
      [langCode]: {
        ...(current[langCode] ?? {}),
        [fieldKey]: value,
      },
    }));
    // Once a human edits the text it is no longer purely AI output, so it stops being marked.
    this.aiTranslatedCodes.update(codes => codes.filter(code => code !== langCode));
    this.failedTranslations.update(current => {
      const byLanguage = current[fieldKey];
      if (!byLanguage) {
        return current;
      }
      const settled = settleFailureOnEdit(byLanguage, langCode, value);
      return settled === byLanguage ? current : { ...current, [fieldKey]: settled };
    });
  }

  protected failureFor(langCode: string, fieldKey: string): FailedTranslation | null {
    return this.failedTranslations()[fieldKey]?.[langCode] ?? null;
  }

  protected isAiTranslated(langCode: string): boolean {
    return this.aiTranslatedCodes().includes(langCode);
  }

  protected isReadonlyField(langCode: string, fieldKey: string): boolean {
    return Boolean(this.data.readonlyFields?.[langCode]?.includes(fieldKey));
  }

  protected isRequired(fieldKey: string): boolean {
    return Boolean(this.sourceValues[fieldKey]?.trim());
  }

  /**
   * Calls the AI translation endpoint for the given target languages.
   * Rail layout passes [selectedLangCode()]; stacked layout passes allLangCodes().
   */
  protected suggestAll(targetLanguages: string[]): void {
    const targetType = this.aiTargetType();
    const targetUuid = this.aiTargetUuid();
    if (!targetType || !targetUuid || this.isSuggesting() || !this.isAiEnabled()) {
      return;
    }

    // The endpoint's maxLength caps the whole request, but fields can carry
    // different limits — group texts by maxLength so each request is capped to
    // the same limit the form and backend enforce for those fields.
    const textsByMaxLength = new Map<number | undefined, Record<string, string>>();
    for (const field of this.fields) {
      const source = this.sourceValues[field.key]?.trim();
      if (!source) {
        continue;
      }
      const group = textsByMaxLength.get(field.maxLength) ?? {};
      group[field.key] = source;
      textsByMaxLength.set(field.maxLength, group);
    }
    if (textsByMaxLength.size === 0) {
      return;
    }

    this.isSuggesting.set(true);

    const requests = [...textsByMaxLength].map(([maxLength, texts]) =>
      this.aiTranslateService.translate$(targetType, targetUuid, {
        sourceLanguage: this.data.defaultLanguage?.code as ApiTypes.LanguageEnum,
        targetLanguages: targetLanguages as ApiTypes.LanguageEnum[],
        texts,
        ...(maxLength === undefined ? {} : { options: { maxLength } }),
      })
    );

    forkJoin(requests)
      .pipe(
        finalize(() => this.isSuggesting.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: responses =>
          this.applyTranslations(
            responses,
            [...textsByMaxLength.values()].flatMap(texts => Object.keys(texts)),
            targetLanguages
          ),
        error: (err: unknown) => this.handleSuggestError(err),
      });
  }

  protected save(): void {
    if (this.hasTextOverLimit()) {
      return;
    }
    const result: TranslateDialogResult = {};
    for (const field of this.fields) {
      result[field.key] = this.allLangs.map(lang => ({
        lang: lang.code,
        value: this.values()[lang.code]?.[field.key] ?? '',
      }));
      if (this.data.defaultLanguage) {
        result[field.key].push({
          lang: this.data.defaultLanguage.code,
          value: this.sourceValues[field.key] ?? '',
        });
      }
    }
    this.dialogRef.close(result);
  }

  /**
   * A language that failed keeps whatever text it already had — possibly written by a person — but
   * loses its AI mark unless another field in it did get a suggestion.
   */
  private applyTranslations(
    responses: ApiTypes.TranslateResponseDto[],
    requestedFieldKeys: string[],
    requestedLanguages: string[]
  ): void {
    const outcome = readAiTranslations(responses);

    this.values.update(current => {
      const next = { ...current };
      for (const [fieldKey, byLanguage] of Object.entries(outcome.translations)) {
        for (const [langCode, text] of Object.entries(byLanguage)) {
          next[langCode] = { ...(next[langCode] ?? {}), [fieldKey]: text };
        }
      }
      return next;
    });

    this.failedTranslations.update(current =>
      mergeFailedTranslations(current, outcome.failures, requestedFieldKeys, requestedLanguages)
    );
    this.aiTranslatedCodes.update(codes => [
      ...new Set([
        ...codes.filter(code => !outcome.failedCodes.includes(code)),
        ...outcome.translatedCodes,
      ]),
    ]);
    if (outcome.failedFieldCount > 0) {
      this.notificationService.warnWithKey('SHARED.TRANSLATE_DIALOG.AI_PARTIAL_ERROR');
    }
  }

  private isFieldDone(langCode: string, fieldKey: string): boolean {
    return (
      Boolean(this.getValue(langCode, fieldKey).trim()) && !this.failureFor(langCode, fieldKey)
    );
  }

  private handleSuggestError(error: unknown): void {
    const status = (error as { status?: number })?.status;
    if (status === 429) {
      this.notificationService.warnWithKey('SHARED.TRANSLATE_DIALOG.AI_RATE_LIMITED');
    } else {
      this.notificationService.errorSnackbarWithKey('SHARED.TRANSLATE_DIALOG.AI_ERROR');
    }
  }

  private buildInitialValues(): Record<string, Record<string, string>> {
    const map: Record<string, Record<string, string>> = {};
    for (const lang of this.allLangs) {
      map[lang.code] = {};
      for (const field of this.fields) {
        map[lang.code][field.key] =
          this.data.existingTranslations[field.key]?.find(e => e.lang === lang.code)?.value ?? '';
      }
    }
    return map;
  }
}
